/**
 * OperationsQuality.tsx — Operations / Quality Checks (M2).
 *
 * The mockup's Quality Checks view (klaxiora-crm-mockup-full.html
 * :1984-2004): three KPI cards, a checks table (Checked / Checked by /
 * Result / Notes / Date, :5270) with "Mark Followed Up" on open fails
 * (:5274), and a Log-Quality-Check flow (modal :3178-3211: subject,
 * checked-by people select, Pass/Fail, notes). Backed for real by
 * /v1/operations/quality-checks (0100); checkers are employees (decision
 * M0), picked from the live active-employee list — the SOPs owner-picker
 * pattern. Create-only drawer + row-level follow-up: checks carry NO edit
 * or delete, matching the mockup and 0100's no-DELETE grant. Writes
 * re-fetch — the server row is authoritative (the reloadKey pattern).
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { OpsMetric } from './OperationsOverview'
import { useAsync, fetchQualityChecks, createQualityCheck, followUpQualityCheck, fetchDirectory } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay, success, danger } from '../../../../theme/tokens'
import { QC_RESULTS, qcResultLabel, qcCountLine, qcKpis } from './qualityView'
import { formatSopDate } from './sopsView'

// ── Log Check drawer (create-only — checks are not editable) ─────────────────

function LogCheckDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [subject, setSubject] = useState('')
  const [result, setResult] = useState('pass')
  const [checkedBy, setCheckedBy] = useState('')
  const [notes, setNotes] = useState('')

  // Checker picklist = live ACTIVE employees (the SOPs owner-picker pattern);
  // blank = "not recorded" — the mockup's own table dashes a missing checker
  // (:5278), so absence is a rendered state, not an error.
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!subject.trim()) { toast.error('Subject is required'); return }
    try {
      await createQualityCheck({ subject: subject.trim(), result, notes, checkedBy: checkedBy || undefined })
      toast.success('Quality check logged')
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to log quality check'))
    }
  }

  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-faint transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">Log Quality Check</div>
            <div className="text-12 text-ink-muted mt-1">Record the result of a spot-check against a standard.</div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-faint hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div>
            <label className={labelCls}>What was checked<span className="text-danger ml-1">*</span></label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)}
              placeholder="e.g. Client Onboarding Checklist followed correctly" autoFocus className={inputCls} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Checked by</label>
              {employees.error ? (
                <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
              ) : (
                <select value={checkedBy} onChange={e => setCheckedBy(e.target.value)}
                  disabled={employees.loading} className={inputCls}>
                  <option value="">Not recorded</option>
                  {(employees.data ?? []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className={labelCls}>Result</label>
              <select value={result} onChange={e => setResult(e.target.value)} className={inputCls}>
                {QC_RESULTS.map(r => <option key={r.slug} value={r.slug}>{r.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Anything worth noting..." rows={5}
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          <button onClick={onClose}
            className="k-btn-secondary">
            Cancel
          </button>
          <button onClick={handleSave}
            className="k-btn">
            Log Check
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

function ResultBadge({ result }: { result: string }) {
  const tone = result === 'pass'
    ? { background: success.bg, color: success.text }
    : { background: danger.bg, color: danger.text }
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium" style={tone}>
      {qcResultLabel(result)}
    </span>
  )
}

export function OperationsQuality() {
  const [reloadKey, setReloadKey] = useState(0)
  const [showDrawer, setShowDrawer] = useState(false)
  const { data, loading, error } = useAsync(fetchQualityChecks, [reloadKey])

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const checks = data ?? []
  const kpis = qcKpis(checks, new Date())

  async function handleFollowUp(id: string) {
    try {
      await followUpQualityCheck(id)
      toast.success('Marked as followed up')
      setReloadKey(k => k + 1)
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to mark as followed up'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Quality Checks"
        subtitle={checks.length === 0 ? 'Spot-checks against a standard, and the result.' : qcCountLine(checks.length)}
        action={
          <button className="k-btn inline-flex items-center gap-2" onClick={() => setShowDrawer(true)}>
            <Plus size={14} /> Log Check
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-3">
        <OpsMetric label="Checks this month" value={kpis.thisMonth} note="Calendar month" />
        <OpsMetric label="Pass rate" value={kpis.passRate === null ? '—' : `${kpis.passRate}%`} note="Across all checks" />
        <OpsMetric label="Failed — needs follow-up" value={kpis.failedOpen} note="Open" />
      </div>

      <div className="k-card">
        {checks.length === 0 ? (
          <EmptyState
            message="No checks logged yet"
            hint="Log a quality check and the result will appear here"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table ops-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Checked</th>
                  <th className="text-left">Checked by</th>
                  <th className="text-left">Result</th>
                  <th className="text-left">Notes</th>
                  <th className="text-left">Date</th>
                  <th className="text-left"></th>
                </tr>
              </thead>
              <tbody>
                {checks.map(c => (
                  <tr key={c.id}>
                    <td className="text-13 font-medium text-ink-primary">{c.subject}</td>
                    <td className="text-12 text-ink-secondary">{c.checkedByName || '—'}</td>
                    <td><ResultBadge result={c.result} /></td>
                    <td className="text-12 text-ink-secondary" style={{ maxWidth: 260 }}>{c.notes || '—'}</td>
                    <td className="text-12 text-ink-secondary whitespace-nowrap">{formatSopDate(c.createdAt)}</td>
                    <td className="whitespace-nowrap">
                      {c.result === 'fail' && !c.resolved && (
                        <button className="k-btn-secondary" onClick={() => handleFollowUp(c.id)}>
                          Mark Followed Up
                        </button>
                      )}
                      {c.result === 'fail' && c.resolved && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium"
                          style={{ background: success.bg, color: success.text }}>
                          Followed up
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDrawer && (
        <LogCheckDrawer
          onClose={() => setShowDrawer(false)}
          onSaved={() => setReloadKey(k => k + 1)}
        />
      )}
    </div>
  )
}
